import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { label: "Ana Sayfa", to: "/" },
  { label: "Kurumsal", to: "/kurumsal" },
  { label: "Hizmetler", to: "/hizmetler" },
  { label: "Çözümler", to: "/cozumler" },
  { label: "Projeler", to: "/projeler" },
  { label: "İletişim", to: "/iletisim" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          transparent
            ? "bg-transparent border-b border-transparent"
            : "bg-[#050e1f]/92 backdrop-blur-xl border-b border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.35)]"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 flex items-center justify-between h-[68px] md:h-[78px]">
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
            <img
              src="/sem-mark-white.png"
              alt="Sistem Elektrik ve Mühendislik"
              className="h-9 md:h-11 w-auto transition-transform duration-500 group-hover:scale-[1.03]"
            />
            <span className="hidden sm:block leading-none">
              <span className="block text-white font-head font-extrabold tracking-tight text-sm">SİSTEM ELEKTRİK</span>
              <span className="block text-silver/70 text-[10px] tracking-[0.25em] uppercase">ve Mühendislik</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={`relative text-sm font-medium tracking-wide transition-colors duration-300 ${
                  pathname === l.to ? "text-cyan" : "text-white/80 hover:text-white"
                }`}
              >
                {l.label}
                {pathname === l.to && (
                  <motion.span layoutId="nav-underline" className="absolute -bottom-2 left-0 right-0 h-px bg-cyan" />
                )}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/iletisim"
              data-testid="navbar-cta"
              className="hidden md:inline-flex items-center gap-2 bg-cyan hover:bg-tech text-navy hover:text-white font-semibold text-sm px-5 py-2.5 transition-colors duration-300 clip-tag"
            >
              TEKLİF AL <ArrowUpRight className="w-4 h-4" />
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              className="lg:hidden text-white p-2"
              aria-label="Menüyü aç"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-navy lg:hidden"
            initial={{ opacity: 0, clipPath: "circle(0% at 90% 8%)" }}
            animate={{ opacity: 1, clipPath: "circle(140% at 90% 8%)" }}
            exit={{ opacity: 0, clipPath: "circle(0% at 90% 8%)" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="grid-lines-dark absolute inset-0 opacity-40" />
            <div className="relative flex items-center justify-between h-[72px] px-5">
              <img src="/sem-mark-white.png" alt="SEM" className="h-9 w-auto" />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-white p-2" aria-label="Menüyü kapat">
                <X className="w-7 h-7" />
              </button>
            </div>
            <nav className="relative px-6 mt-10 flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.06 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    className="block py-4 border-b border-white/10 text-3xl font-head font-bold text-white"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <Link to="/iletisim" className="mt-8 inline-flex items-center justify-center gap-2 bg-cyan text-navy font-semibold px-6 py-4">
                TEKLİF AL <ArrowUpRight className="w-5 h-5" />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
